import React, {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
} from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type TableSelection } from "@devexpress/dx-react-grid-bootstrap4";
import { useActiveTab } from "@features/sales-v3/hooks/use-active-tab";
import { useTabState } from "@features/sales-v3/hooks/use-tab-state";
import { useSplitView } from "@features/sales-v3/hooks/use-table-selection";
import { CustomCheckbox } from "@shared/custom-checkbox";

import { useExpandedRowIds } from "../../contexts";

export const TableSelectionHeaderCell = ({
  someSelected,
  onToggle,
}: PropsWithChildren<TableSelection.HeaderCellProps>) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { activeTab } = useActiveTab();
  const { tabState, setTabState } = useTabState(activeTab);
  const [, setExpandedRowIds] = useExpandedRowIds();
  const checkboxRef = useRef<HTMLInputElement>(null);
  const isSplitView = useSplitView();

  const isIntermediate = useMemo(() => {
    return Boolean(
      tabState.proposedFiltersState.exclusionAdKeys.length ||
        tabState.proposedFiltersState.exclusionAtomicities.length ||
        tabState.proposedFiltersState.inclusionAdKeys.length ||
        tabState.proposedFiltersState.inclusionAtomicities.length,
    );
  }, [
    tabState.proposedFiltersState.exclusionAdKeys.length,
    tabState.proposedFiltersState.exclusionAtomicities,
    tabState.proposedFiltersState.inclusionAdKeys.length,
    tabState.proposedFiltersState.inclusionAtomicities.length,
  ]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIntermediate;
    }
  }, [isIntermediate, someSelected]);

  return (
    <th
      className={clsx(
        "left-0 dx-g-bs4-header-cell text-nowrap border-right",
        "dx-g-bs4-fixed-cell position-sticky z-1",
      )}
    >
      {isSplitView ? (
        <div className="flex items-center justify-center w-full h-full">
          <GenericTooltip
            content="Remove selections"
            noInfoCircle
            placement="top"
          >
            <div
              className={clsx(
                "h-6 w-6 flex-center cursor-pointer bg-cosmic-3",
                "hover:bg-primary text-primary hover:text-white rounded-lg",
              )}
            >
              <i
                className="fa-sharp fa-regular fa-times-circle"
                onClick={() => {
                  prettyLogEventPage({
                    domain: "Table",
                    action: "onClick",
                    additionalActionInfo: "Reset Tab State",
                  });

                  setTabState((tabState) => ({
                    ...tabState,
                    proposedFiltersState: {
                      inclusionAtomicities: [],
                      exclusionAtomicities: [],
                      inclusionAdKeys: [],
                      exclusionAdKeys: [],
                      campaignInclusionAdKeys: [],
                      campaignExclusionAdKeys: [],
                      adsetInclusionAdKeys: [],
                      adsetExclusionAdKeys: [],
                    },
                  }));
                }}
              />
            </div>
          </GenericTooltip>
        </div>
      ) : (
        <div className="flex items-center justify-center w-full h-full">
          <CustomCheckbox
            ref={checkboxRef}
            checked={tabState.selectAll}
            onChange={() => {
              prettyLogEventPage({
                domain: "Table",
                action: "onClick",
                additionalActionInfo: "Set Tab State",
              });

              setTabState((tabState) => ({
                ...tabState,
                proposedFiltersState: {
                  inclusionAtomicities: [],
                  exclusionAtomicities: [],
                  inclusionAdKeys: [],
                  exclusionAdKeys: [],
                  campaignInclusionAdKeys: [],
                  campaignExclusionAdKeys: [],
                  adsetInclusionAdKeys: [],
                  adsetExclusionAdKeys: [],
                },
                selectAll: !tabState.selectAll,
              }));
              setExpandedRowIds([]);
            }}
          />
        </div>
      )}
      <div className="absolute h-[45%] w-[1px] top-[27.5%] right-0 bg-gray-1" />
    </th>
  );
};
